import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Input, Button, Space } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import AxiosOICStat from 'AxiosOICStat';
import { message } from 'antd';

const { Option } = Select;

const EditSdgMappingModal = ({visible, onCancel, onUpdate, editingRecord, editForm, sdgSeries, indicatorOptions, dimensionKeys, dimensionOptions, loadingDimensions, loading, dimensionFieldMapping}) => {
  const [activeDimensions, setActiveDimensions] = useState([]);

  useEffect(() => {
    if (editingRecord && dimensionKeys.length > 0) {
      const nonEmptyDims = dimensionKeys.filter((key) => {
        const fieldName = dimensionFieldMapping[key] || key;
        const value = editingRecord[fieldName];
        return value !== null && value !== undefined && String(value).trim() !== "";
      });
      setActiveDimensions(nonEmptyDims);
    }
  }, [editingRecord, dimensionKeys, dimensionFieldMapping]);

  const handleSeriesCodeChange = (seriesCode) => {
    const selectedSeries = sdgSeries.find(series => series.code === seriesCode);
    if (selectedSeries) {
      editForm.setFieldsValue({
        SeriesCode: selectedSeries.code,
        SeriesDescription: selectedSeries.description,
        Indicator: Array.isArray(selectedSeries.indicator) ? selectedSeries.indicator.join(', ') : selectedSeries.indicator
      });
    }
  };

  const getOptionsForDimension = (dimensionKey) => {
    return dimensionOptions[dimensionKey] || [];
  };

  const handleEditSubmit = async (values) => {
    try {
      const allDimensionFields = {};
      dimensionKeys.forEach(key => {
        const fieldName = dimensionFieldMapping[key] || key;
        allDimensionFields[fieldName] = null;
      });

      const finalValues = {
        ...allDimensionFields,
        ...values
      };

      await AxiosOICStat.put(`/external_api/sdg/sdg-mapping/${editingRecord.id}`, finalValues);
      message.success('SDG mapping updated successfully');
      onUpdate(finalValues);
      onCancel();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        'Failed to update SDG mapping. Please try again.';
      message.error(detail);
    }
  };

  const handleAddDimension = (dimensionKey) => {
    if (!activeDimensions.includes(dimensionKey)) {
      setActiveDimensions([...activeDimensions, dimensionKey]);
    }
  };

  const handleRemoveDimension = (dimensionKey) => {
    setActiveDimensions(activeDimensions.filter(d => d !== dimensionKey));
    const fieldName = dimensionFieldMapping[dimensionKey] || dimensionKey;
    editForm.setFieldsValue({ [fieldName]: null });
  };

  const availableDimensionsToAdd = dimensionKeys.filter(key => !activeDimensions.includes(key));

  return (
    <Modal
      title="Edit SDG Mapping"
      open={visible}
      onOk={() => editForm.submit()}
      onCancel={onCancel}
      okText="Save Changes"
      cancelText="Cancel"
      width={800}
      confirmLoading={loading}
    >
      <Form
        form={editForm}
        layout="vertical"
        onFinish={handleEditSubmit}
      >
        <Form.Item
          name="SeriesCode"
          label="Series Code"
          rules={[{ required: true, message: 'Please select series code' }]}
        >
          <Select
            showSearch
            placeholder="Select Series Code"
            onChange={handleSeriesCodeChange}
            filterOption={(input, option) => {
              const optionText = `${option.value} - ${sdgSeries.find(s => s.code === option.value)?.description || ''}`;
              return optionText.toLowerCase().includes(input.toLowerCase());
            }}
          >
            {sdgSeries.map(series => (
              <Option key={series.code} value={series.code}>
                {series.code} - {series.description}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="SeriesDescription"
          label="Series Description"
          rules={[{ required: true, message: 'Please enter series description' }]}
        >
          <Input.TextArea rows={3} disabled />
        </Form.Item>

        <Form.Item
          name="Indicator"
          label="SDG Indicator"
        >
          <Input placeholder="e.g., 3.1.1" disabled />
        </Form.Item>

        <Form.Item
          name="ind_code"
          label="Indicator Code"
          rules={[{ required: true, message: 'Please select indicator' }]}
        >
          <Select
            showSearch
            placeholder="Select Indicator"
            options={indicatorOptions}
            filterOption={(input, option) =>
              option.label.toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>

        {activeDimensions.map((dimensionKey) => {
          const options = getOptionsForDimension(dimensionKey);
          const fieldName = dimensionFieldMapping[dimensionKey] || dimensionKey;

          return (
            <Form.Item
              key={dimensionKey}
              label={
                <Space>
                  <span>{dimensionKey}</span>
                  <MinusCircleOutlined
                    style={{ color: 'red', cursor: 'pointer' }}
                    onClick={() => handleRemoveDimension(dimensionKey)}
                  />
                </Space>
              }
              name={fieldName}
            >
              {options.length > 0 ? (
                <Select
                  showSearch
                  allowClear
                  placeholder={`Select ${dimensionKey}`}
                  loading={loadingDimensions}
                  filterOption={(input, option) =>
                    String(option.value).toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {options.map(option => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              ) : (
                <Input
                  placeholder={`Enter ${dimensionKey}`}
                  disabled={loadingDimensions}
                />
              )}
            </Form.Item>
          );
        })}

        {availableDimensionsToAdd.length > 0 && (
          <Form.Item>
            <Select
              placeholder="Add a dimension"
              style={{ width: '100%' }}
              onChange={handleAddDimension}
              value={null}
              suffixIcon={<PlusOutlined />}
            >
              {availableDimensionsToAdd.map(key => (
                <Option key={key} value={key}>
                  {key}
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

      </Form>
    </Modal>
  );
};

export default EditSdgMappingModal;